<template>
  <section class="finances__widget finances__widget--large finances__widget--graph">
    <header class="finances__widget-header">
      <ul class="finances__widget-menu">
        <li
          :class="graphType === 'revenue' && 'finances__widget-menu-item--active'"
          class="finances__widget-menu-item"
          @click="graphType = 'revenue'"
        >
          Receitas
        </li>

        <li
          :class="graphType === 'expense' && 'finances__widget-menu-item--active'"
          class="finances__widget-menu-item"
          @click="graphType = 'expense'"
        >
          Despesas
        </li>
      </ul>

      <div class="finances__widget-filter">
        <div
          v-click-outside="() => calendars.showStart = false"
          class="finances__widget-date"
        >
          <div
            :class="calendars.showStart && 'active'"
            class="finances__widget-picker"
            @click="calendars.showStart = !calendars.showStart"
          >
            <span>
              {{ $moment(filters.start).format('DD/MM/YYYY') }}
            </span>

            <dr-icon
              :color="'#8E8E92'"
              class="agenda-settings__date-picker--icon"
              name="dashCalendar"
            />
          </div>

          <div class="finances__widget-calendar">
            <dr-calendar
              v-show="calendars.showStart"
              :key="'calendarShowStart'"
              v-model="filters.start"
              @change="calendars.showStart = false"
            />
          </div>
        </div>

        <div
          v-click-outside="() => calendars.showEnd = false"
          class="finances__widget-date"
        >
          <div
            :class="calendars.showEnd && 'active'"
            class="finances__widget-picker"
            @click="calendars.showEnd = !calendars.showEnd"
          >
            <span>
              {{ $moment(filters.end).format('DD/MM/YYYY') }}
            </span>

            <dr-icon
              :color="'#8E8E92'"
              class="agenda-settings__date-picker--icon"
              name="dashCalendar"
            />
          </div>

          <div class="finances__widget-calendar">
            <dr-calendar
              v-show="calendars.showEnd"
              :key="'calendarShowEnd'"
              v-model="filters.end"
              @change="calendars.showEnd = false"
            />
          </div>
        </div>
      </div>
    </header>

    <div class="finances__widget-body finances__widget-body--graph">
      <div class="finances__widget-graph">
        <transition name="fade">
          <g-chart
            v-if="!loading"
            :packages="{ 'packages': ['corechart']  }"
            :data.sync="chartData"
            :options.sync="chartOptions"
            type="ColumnChart"
            language="pt-br"
            class="chart"
          />
        </transition>

        <transition name="fade">
          <div
            v-if="loading"
            :key="'widget-summary-loading'"
            class="finances__widget-loading"
          >
            <dr-loader size="lg" />
          </div>
        </transition>
      </div>

      <div class="finances__widget-summary">
        <h4 class="finances__widget-title finances__widget-title--no-stroke">
          Maiores {{ (graphType === 'revenue') ? 'receitas' : 'despesas' }}
        </h4>

        <transition name="fade">
          <ol v-if="!loading" class="finances__top-values">
            <li
              v-for="(item, index) in topValues"
              :key="`topValuesItem_${index}`"
              :class="
                (graphType === 'revenue')
                ? 'finances__top-values-item--revenue'
                : 'finances__top-values-item--expense'
              "
              class="finances__top-values-item"
            >
              <span class="finances__top-values-item--col">
                {{ $moment(item.date, 'YYYY-MM-DD', true).format('DD MMMM YYYY') }}
              </span>

              <span class="finances__top-values-item--col">
                R$ {{ item.value | TO_BRL }}
              </span>
            </li>
          </ol>
        </transition>

        <transition name="fade">
          <div
            v-if="loading"
            :key="'widget-summary-loading'"
            class="finances__widget-loading"
          >
            <dr-loader size="lg" />
          </div>
        </transition>
      </div>

    </div>

    <footer class="finances__widget-footer finances__widget-footer--padded">
      <transition name="fade">
        <router-link
          v-if="!loading"
          :disabled="!Array.isArray(financialCategories) || financialCategories.length === 0"
          :to="`/${fn_GET_PARENT_ROUTE($route)}/finances/list`"
          tag="a"
          class="btn btn-primary"
        >
          Ver mais
        </router-link>
      </transition>

      <!-- Barra gradiente a ser aplicada no gráfico -->
      <svg style="width:0;height:0;position:absolute;" aria-hidden="true" focusable="false">
        <linearGradient id="gradient-revenue" x1="1" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="rgba(80, 227, 194, 0.85)" />
          <stop offset="100%" stop-color="#4bace5" />
        </linearGradient>

        <linearGradient id="gradient-expense" x1="1" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#FFAA85" />
          <stop offset="100%" stop-color="#B3315F" />
        </linearGradient>
      </svg>
    </footer>
  </section>
</template>

<script>
import { DrCalendar } from 'cm2tech-vue-calendar'
import { GChart } from 'vue-google-charts'
import EventBus from '@/globals/eventBus'
import { mapGetters } from 'vuex'

export default {
  name: 'expenseRevenueGraph',

  props: {
    financialCategories: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  components: {
    DrCalendar,
    GChart
  },

  data () {
    return {
      loading: false,
      graphType: 'revenue',
      calendars: {
        showStart: false,
        showEnd: false
      },
      filters: {
        start: null,
        end: null
      },
      topValues: [],
      chartData: [],
      chartOptions: {}
    }
  },

  computed: {
    ...mapGetters({
      office: 'getCurrentOffice'
    }),

    getGraphLeft () {
      return this.fn_IS_MOBILE() ? '15%' : '1rem'
    },

    getGraphWidth () {
      return this.fn_IS_MOBILE() ? '90%' : '90%'
    },

    getWindowWidth () {
      return this.$store.getters.getViewport.width
    }
  },

  watch: {
    office: {
      deep: true,
      immediate: false,
      handler () {
        this.init()
      }
    },

    'filters.start': {
      deep: true,
      immediate: false,
      handler () {
        const start = this.$moment(this.filters.start)
        const end = this.$moment(this.filters.end)

        if (start.isAfter(end, 'day')) {
          this.filters.start = this.$moment(this.filters.end).toISOString()
        }

        this.init()
      }
    },

    'filters.end': {
      deep: true,
      immediate: false,
      handler () {
        const start = this.$moment(this.filters.start)
        const end = this.$moment(this.filters.end)

        if (end.isBefore(start, 'day')) {
          this.filters.end = this.$moment(this.filters.start).toISOString(0)
        }
        this.init()
      }
    },

    graphType: {
      deep: false,
      immediate: false,
      handler () {
        this.init()
      }
    },

    getWindowWidth (next) {
      this.init()
    }

  },

  created () {
    this.filters.start = this.$moment().startOf('y').toISOString()
    this.filters.end = this.$moment().toISOString()

    EventBus.$on('reload-finance', () => this.init())
    this.init()
  },

  mounted () {
    const graphArea = document.querySelector('.finances__widget-graph')
    const svg = document.querySelector('svg')
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')

    const observer = new MutationObserver(() => {
      graphArea.querySelectorAll('rect').forEach((rect) => {
        if (/#4bace5/gi.test(rect.getAttribute('fill'))) {
          rect.setAttribute(
            'fill',
            'url(#gradient-revenue) #4bace5'
          )
        }

        if (/#B3315F/gi.test(rect.getAttribute('fill'))) {
          rect.setAttribute(
            'fill',
            'url(#gradient-expense) #B3315F'
          )
        }
      })
    })

    observer.observe(graphArea, {
      childList: true,
      subtree: true
    })
  },

  methods: {
    init () {
      const initialDate = this.$moment(this.filters.start).format('YYYY-MM-DD')
      const finalDate = this.$moment(this.filters.end).format('YYYY-MM-DD')
      const prefix = this.fn_GET_PARENT_ROUTE(this.$route)
      const suffix = `?initialDate=${initialDate}&finalDate=${finalDate}&filterNot[fs.status]=CANCELADO`
      const url = `/${prefix}/doctor_office/${this.office.id}/financial_statement/daily_balance${suffix}`

      this.loading = true
      this.$http
        .get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          if (res.status === 200) {
            const { data } = res
            this.chartData = this.consumeData(data)
            this.chartOptions = this.setChartOptions(this.graphType)
            this.topValues = this.filterTopValues(data, this.graphType, 6)
          }
        })
        .catch(err => console.log('err', err))
        .finally(() => (this.loading = false))
    },

    consumeData (data) {
      const monthyear = this.getUniqueYearMonth(data)
      const filtered = this.filterByMonth(data, monthyear)
      const reduced = this.reduceData(filtered)

      return this.createChartData(reduced, this.graphType)
    },

    createChartData (data, type = 'revenue') {
      const chartData = [
        [
          'Valor',
          (type === 'revenue') ? 'Receitas' : 'Despesas',
          { role: 'style' }
        ]
      ]

      Object.keys(data).forEach((month) => {
        const monthName = this.$moment(month, 'YYYY-MM', true).format('MMM/YYYY')
        chartData.push([
          monthName,
          data[month][type],
          `color: ${(type === 'revenue') ? '#4bace5' : '#B3315F'}; stroke-width: 0;`
        ])
      })

      return chartData
    },

    setChartOptions (type) {
      return {
        height: 300,
        width: 'auto',
        legend: { position: 'none' },
        chartArea: {
          left: this.getGraphLeft,
          top: '1rem',
          width: this.getGraphWidth,
          height: '80%'
        },
        bar: {
          groupWidth: 30,
          strokeWidth: 0
        },
        vAxis: {
          baselineColor: 'rgb(74, 74 ,74)',
          format: 'R$ #',
          textStyle: {
            fontName: 'Open Sans',
            fontSize: '11',
            fontWeight: '600',
            color: 'rgb(74, 74, 74)'
          },
          gridlines: {
            color: 'rgb(220, 220, 220)'
          }
        },
        hAxis: {
          textStyle: {
            fontName: 'Open Sans',
            fontSize: '12',
            fontWeight: '600',
            color: 'rgb(74, 74, 74)'
          },
          baselineColor: 'rgb(74, 74 ,74)',
          gridlines: {
            color: 'rgb(220, 220, 220)'
          }
        }
      }
    },

    filterTopValues (data, type, number) {
      const topValues = []

      for (const item in data) {
        topValues.push({
          date: item,
          value: parseFloat(data[item][type])
        })
      }

      return topValues
        .sort((a, b) => Math.abs(a.value) - Math.abs(b.value))
        .reverse()
        .splice(0, number)
    },

    reduceData (data) {
      const reduced = { ...data }

      for (const key in reduced) {
        reduced[key] = { revenue: 0, expense: 0 }
      }

      for (const month in data) {
        reduced[month].revenue = data[month].reduce(
          (total, item) => {
            return (total = total + parseFloat(item.revenue))
          }, 0)

        reduced[month].expense = data[month].reduce(
          (total, item) => {
            return (total = total + (Math.abs(parseFloat(item.expense))))
          }, 0)
      }

      return reduced
    },

    filterByMonth (data, months) {
      const filtered = {}
      Object.entries({ ...months }).forEach(key => (filtered[key[1]] = []))

      months.forEach((m) => {
        const key = String(m)
        const monthMoment = this.$moment(m, 'YYYY-MM', true)
        const some = Object.entries(data).filter(
          (item) => {
            const itemMoment = this.$moment(item[0], 'YYYY-MM-DD', true)

            if (this.$moment(itemMoment).isSame(monthMoment, 'month')) {
              return { ...item[1] }
            }
          }
        )

        some.forEach(i => (filtered[key].push({ ...i[1] })))
      })

      return filtered
    },

    getUniqueYearMonth (obj) {
      const dates = []

      Object.keys(obj).forEach((key) => {
        const moment = this.$moment(key, 'YYYY-MM-DD', true)
        const year = moment.format('YYYY')
        const month = moment.format('MM')
        dates.push(`${year}-${month}`)
      })

      return [...new Set(dates)]
    }
  }
}
</script>
